/**
 * task-routes.ts — the Aufgaben → target-screen deep-link contract.
 *
 * Small pure link helpers, one per routable task family, mirroring the
 * invoiceTaskLink precedent (features/invoices/task-link.ts). Each returns a
 * router target or null; the Aufgaben screen tries them in order and falls back
 * to the decision peek for everything unrouted (mandate_signing included — there
 * is no /vorgaenge route yet, so a Mandat opens the peek).
 *
 * [Source: apps/web/src/features/invoices/task-link.ts (invoiceTaskLink)]
 * [Source: packages/shared/src/task.ts TaskProcess]
 */

import type { TaskProcess } from '@brokly/shared';
import { invoiceTaskLink } from '../invoices/task-link';

/** The minimal task shape every link helper reads. */
export interface RoutableTask {
  process: TaskProcess;
  subject_type: string | null;
  subject_id: string | null;
}

/** A renewal task (`renewal_due`) → the Renewals board. */
export interface RenewalTaskLink {
  to: '/renewals';
}

/** A commission-match task (`commission_manual_match`) → the Courtage area. */
export interface CommissionTaskLink {
  to: '/courtage';
}

/** Map a renewal task to /renewals, or null when it is not a renewal task. */
export function renewalTaskLink(task: Pick<RoutableTask, 'process'>): RenewalTaskLink | null {
  return task.process === 'renewal_due' ? { to: '/renewals' } : null;
}

/** Map a commission-match task to /courtage, or null otherwise. */
export function commissionTaskLink(
  task: Pick<RoutableTask, 'process'>,
): CommissionTaskLink | null {
  return task.process === 'commission_manual_match' ? { to: '/courtage' } : null;
}

/** A resolved navigation target for a row click (or `null` → open the peek). */
export type TaskRouteTarget =
  | { to: '/rechnungen'; search: { invoice: string } }
  | RenewalTaskLink
  | CommissionTaskLink;

/**
 * Resolve a task to its deep-link target, or null when the row should open the
 * decision peek instead (mandate_signing + every non-routable process).
 * Order matters: invoice family → renewal → commission → peek.
 */
export function resolveTaskRoute(task: RoutableTask): TaskRouteTarget | null {
  return (
    invoiceTaskLink(task) ?? renewalTaskLink(task) ?? commissionTaskLink(task) ?? null
  );
}
